import { t } from '@reos/i18n';
import { baseUrl } from './api.js';

/**
 * Klient Connector Lab rout (packages/contexts/listing/src/public/plugin.ts).
 * UI capabilities NIKDY nehardcoduje (§31) - vse cte z /v1/connectors/registry;
 * tady jsou jen typy + ceske popisky stavu (stejny pristup jako ostatni
 * web-admin stranky, i18n vrstva web-adminu zatim neexistuje).
 */

export interface CapabilityClaim {
  readonly support: 'SUPPORTED' | 'UNSUPPORTED' | 'UNKNOWN';
  readonly claim: 'VERIFIED_PUBLIC_DOC' | 'ASSUMPTION_TO_VERIFY' | 'PENDING_SPEC' | 'UNKNOWN';
  readonly requires?: readonly string[];
  readonly sourceRef?: string;
  readonly note?: string;
}

export interface PortalDescriptor {
  readonly portal: string;
  readonly name: string;
  readonly priority: number;
  readonly status: 'DOCS_VERIFIED' | 'PENDING_SPEC' | 'PARTNER_REQUIRED';
  /** Krok SR1: rodina napojeni a trh portalu (registr, nikdy hardcode v UI). */
  readonly protocolFamily: string;
  readonly market: 'CZ' | 'SK' | 'INTERNATIONAL';
  readonly docsCheckedAt: string | null;
  readonly docVersion: string | null;
  readonly specMirror: string | null;
  readonly capabilities: Readonly<Record<string, CapabilityClaim>>;
  readonly limits: Readonly<Record<string, number>>;
  readonly auth: {
    readonly model: string;
    readonly systemCredentialsRequired: boolean;
    readonly tenantCredentialFields: readonly { key: string; kind: 'text' | 'secret' }[];
  };
  readonly testEnvironment: CapabilityClaim;
  /**
   * Krok UXP6: `onboarding` (stav naší žádosti o napojení, kontakt na
   * portál) je provozní info RedHand, API ho tenantovi nevrací — proto
   * tu není. Platformový přístup jde přímo přes `listPortalDescriptors()`
   * v `@reos/listing` (balíček je public API), ne přes tuto DTO.
   */
  readonly connectorImplemented: boolean;
}

/**
 * Krok UXP4b bod 3, oprava 1 (nález P2-8) — KAM JDE ZVEŘEJNIT.
 *
 * „Hotový konektor" (`connectorImplemented`) a „jde tam zveřejnit" nejsou
 * totéž: u portálů se stavem `PARTNER_REQUIRED` konektor existuje, ale bez
 * partnerské smlouvy se neodešle nic. Souhrn „N z M zveřejněno" počítal
 * jmenovatele z první podmínky, Nový inzerát nabízí druhou — makléř pak
 * na jedné obrazovce četl sedm portálů a na druhé čtyři.
 *
 * Serverovým dvojčetem je `availability: 'connected'` v
 * `packages/contexts/listing/src/draft/portals.ts`; že se obě strany
 * neroze­jdou, hlídá `test/draft-portal-names.spec.ts` (část „shoda
 * s registrem"). Web-admin si proto pravidlo neopisuje do komponent —
 * stojí tady, u DTO, ze kterého se čte.
 */
export function canPublishToPortal(descriptor: PortalDescriptor): boolean {
  return descriptor.connectorImplemented && descriptor.status !== 'PARTNER_REQUIRED';
}

export interface ConnectionDto {
  readonly id: string;
  readonly portal: string;
  readonly mode: 'DEMO' | 'TEST' | 'LIVE';
  readonly enabled: boolean;
  readonly hasCredentials: boolean;
  readonly secretHint: string | null;
  readonly updatedAt: string;
}

export interface ValidationIssue {
  readonly severity: 'BLOCKER' | 'WARNING';
  readonly rule: string;
  readonly field: string;
  readonly context?: Readonly<Record<string, string | number>>;
}

export interface ValidationResult {
  readonly portal: string;
  readonly ok: boolean;
  readonly blockers: readonly ValidationIssue[];
  readonly warnings: readonly ValidationIssue[];
  readonly ruleVersion: string;
}

export interface NormalizedPortalError {
  readonly portal: string;
  readonly code: string;
  readonly portalStatus: number | string;
  readonly raw: string;
  readonly retryable: boolean;
}

export interface PublicationDto {
  readonly id: string;
  readonly listingId: string;
  readonly portal: string;
  readonly state: string | null;
  readonly remoteId: string | null;
  readonly remoteUrl: string | null;
  readonly validation: ValidationResult | null;
  readonly lastError: NormalizedPortalError | null;
  readonly lastSyncAt: string | null;
  readonly publishedAt: string | null;
}

export interface DryRunPreview {
  readonly portal: string;
  readonly method: string;
  readonly requestBody: string;
  readonly contentType: string;
  readonly unmapped: readonly { field: string; value: string; reason: string }[];
  readonly validation: ValidationResult;
}

export interface SyncCenterCell {
  readonly listingId: string;
  readonly portal: string;
  readonly status: string;
  readonly publicationId: string | null;
  readonly mode: 'DEMO' | 'TEST' | 'LIVE' | null;
  readonly lastErrorCode: string | null;
  readonly remoteId: string | null;
  /**
   * Krok UXD1b: posledni zmena publikace (`publications.updated_at`) — sloupec
   * plocheho radku Publikaci. Nepovinny zamerne, stejne jako `descriptionFull`
   * u radku zakazky: atrapy `/v1/publications/sync-center` v cizich sadach ho
   * nenesou a chybejici cas se cte jako „Zatím nic", ne jako prazdne misto.
   */
  readonly lastChangeAt?: string | null | undefined;
}

/** Krok LIST1: stav inzeratu — Aktivní / Rezervováno / Prodáno / Staženo. */
export type ListingDealStatus = 'ACTIVE' | 'RESERVED' | 'SOLD' | 'WITHDRAWN';

export interface SyncCenterListing {
  readonly listingId: string;
  readonly internalCode: string;
  /** Krok UXP4c (P3, nalez „Publikace: misto adresy kody ZAK-…"). */
  readonly address: string;
  readonly category: string;
  readonly transaction: string;
  /**
   * Krok LIST1 bod 4. Server ho posila vzdy (`publication-service.ts`), tady
   * je nepovinny zamerne: starsi odpoved i fixtura bez nej se ma precist jako
   * ACTIVE, ne spadnout. Vychozi hodnota je na jednom miste nize.
   */
  readonly dealStatus?: ListingDealStatus;
  /**
   * Krok SEZ1 bod 1: nemovitost inzeratu — podle ni seznam nemovitosti pari
   * radek na stav exportu. Nepovinny stejne jako `dealStatus`: atrapy
   * `/v1/publications/sync-center` v cizich sadach ho nenesou a chybejici
   * vazba se cte jako „inzerat k nemovitosti neznam", ne jako pad obrazovky.
   */
  readonly propertyId?: string | null | undefined;
}

/** Krok LIST1: tolerantni cteni — chybejici stav znamena „Aktivní". */
export const syncCenterDealStatus = (listing: SyncCenterListing): ListingDealStatus =>
  listing.dealStatus ?? 'ACTIVE';

export interface SyncCenterView {
  readonly listings: readonly SyncCenterListing[];
  readonly cells: readonly SyncCenterCell[];
}

interface ProblemBody {
  readonly title?: string;
  readonly detail?: string;
  readonly type?: string;
  readonly traceId?: string;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${baseUrl}${path}`, {
    credentials: 'include',
    headers: { accept: 'application/json', ...(init?.body ? { 'content-type': 'application/json' } : {}) },
    ...init,
  });
  if (!res.ok) {
    const problem = (await res.json().catch(() => null)) as ProblemBody | null;
    const text = problem?.type ?? problem?.detail ?? problem?.title ?? `HTTP ${res.status}`;
    throw new Error(text);
  }
  return (await res.json()) as T;
}

export async function fetchRegistry(): Promise<readonly PortalDescriptor[]> {
  const data = await request<{ portals: PortalDescriptor[] }>('/v1/connectors/registry');
  return data.portals;
}

/**
 * Nazev portalu pro oko maklere — JEDNO MISTO pro kazdou obrazovku, ktera
 * registr cte (`/naklady-inzerce`, `/neprirazene-naklady`).
 *
 * Kdyz registr (jeste) neodpovedel, nevraci se klic rozhrani
 * („reality_idnes") — ten pruchod celou aplikaci cte jako technicky text
 * (oprava e2e 1 kroku KAT1l2) a makler nema duvod ho znat. Nazvy portalu se
 * pritom NEHARDCODUJI (§31): zdrojem zustava registr, tohle je jen poctiva
 * veta pro stav „neznam".
 */
export function portalDisplayName(name: string | undefined): string {
  return name ?? t('webAdmin.portaly.unknownName', { locale: 'cs' });
}

/**
 * Krok PORT4 — CO KANCELAR POTREBUJE K NAPOJENI PORTALU.
 *
 * Server (`/v1/connectors/portal-access`) to sklada z profilu rodiny SR-RPC
 * a ze zapsaneho vysledku `pnpm portals:probe`. UI si odkazy ani stavy
 * neopisuje: zdroj pristupu je KOD (`source`), text k nemu je v i18n.
 */
export interface PortalAccessDto {
  readonly portal: string;
  readonly source:
    | 'SELF_SERVICE'
    | 'EMAIL_REQUEST'
    | 'REGISTRATION'
    | 'PORTAL_ACTIVATION'
    /** Krok PORT14: registrace zaslanim fakturacnich udaju + dobiti kreditu. */
    | 'INVOICE_AND_CREDIT'
    | 'UNKNOWN';
  readonly where: string | null;
  /** Adresa jde jen po nesifrovanem HTTP — zapina vyslovne kancelar. */
  readonly insecureTransport: boolean;
  /** Krok PORT10: portal inzeruje JEN pronajmy (DomuHQ) — prodej neprijme. */
  readonly rentalsOnly: boolean;
  /**
   * Krok PORT11: portal ma kratsi limit popisu nez Sreality (ToReality 3000
   * v „prisnem Sreality rezimu"); `null` = zadny vlastni limit nedokumentuje.
   */
  readonly descriptionMaxChars: number | null;
  /**
   * Krok PORT12: jedno pripojeni = vic webu (B3 Technology ma sest);
   * `null` = portal je sam za sebe.
   */
  readonly group: { readonly operator: string; readonly portals: readonly string[] } | null;
  /** Krok PORT12: uvitaci kredit pro nove kancelare pres Makleřova. */
  readonly welcomeCredit: { readonly amount: string; readonly currency: string } | null;
  /**
   * Krok PORT14: co portal uctuje KANCELARI za inzerat (Bazoš 2 Kc vlozeni,
   * 49 Kc topovani); `null` = portal za inzerat neuctuje nic.
   */
  readonly officeFees?:
    | {
        readonly insert: { readonly amount: string; readonly currency: string };
        readonly top: { readonly amount: string; readonly currency: string };
      }
    | null
    | undefined;
  readonly interfaceState:
    | 'INTERFACE_OK'
    | 'INVALID_ADDRESS'
    | 'UNREACHABLE'
    | 'FORBIDDEN'
    | 'UNEXPECTED_RESPONSE'
    | 'NO_ADDRESS'
    /** Krok PORT10: adresu zname, ale od jejiho pridani jsme nemerili. */
    | 'NOT_MEASURED';
  readonly interfaceVersion: string | null;
  readonly interfaceCheckedAt: string;
}

export async function fetchPortalAccess(): Promise<readonly PortalAccessDto[]> {
  const data = await request<{ portals: PortalAccessDto[] }>('/v1/connectors/portal-access');
  return data.portals;
}

/**
 * Krok PORT8 — ODKAZ NA FEED PRO UNITED CLASSIFIEDS (Nehnuteľnosti.sk,
 * Reality.sk, TopReality.sk).
 *
 * Portal si inzerci stahuje sam z adresy, kterou mu kancelar nahlasi. Token
 * se vraci JEDINE pri vygenerovani (v DB lezi jen jeho hash), takze obrazovka
 * umi rict „odkaz existuje" a nabidnout vygenerovani noveho.
 */
export const UNITED_CLASSIFIEDS_PORTAL = 'nehnutelnosti_sk';

export interface PortalFeedTokenStatus {
  readonly active: boolean;
  readonly updatedAt: string | null;
}

export function fetchUnitedClassifiedsFeedStatus(): Promise<PortalFeedTokenStatus> {
  return request('/v1/connectors/united-classifieds/feed');
}

export async function rotateUnitedClassifiedsFeedToken(): Promise<string> {
  const data = await request<{ token: string }>('/v1/connectors/united-classifieds/feed', {
    method: 'POST',
  });
  return data.token;
}

/**
 * Absolutni URL feedu — stejny vzor jako `calendarFeedUrl` v `calendar-api.ts`
 * (origin z prohlizece, token v dotazu); server zadnou verejnou adresu
 * nekonfiguruje.
 */
export function unitedClassifiedsFeedUrl(token: string): string {
  const base = typeof window === 'undefined' ? '' : window.location.origin;
  return `${base}/api/v1/connectors/united-classifieds/feed.json?token=${encodeURIComponent(token)}`;
}

export async function fetchConnections(): Promise<readonly ConnectionDto[]> {
  const data = await request<{ connections: ConnectionDto[] }>('/v1/connectors/connections');
  return data.connections;
}

export function saveConnection(
  portal: string,
  body: { mode?: 'DEMO' | 'TEST'; enabled?: boolean },
): Promise<ConnectionDto> {
  return request(`/v1/connectors/connections/${portal}`, { method: 'PUT', body: JSON.stringify(body) });
}

export function saveCredentials(
  portal: string,
  fields: Record<string, string>,
): Promise<ConnectionDto> {
  return request(`/v1/connectors/connections/${portal}/credentials`, {
    method: 'PUT',
    body: JSON.stringify({ fields }),
  });
}

export function fetchSyncCenter(): Promise<SyncCenterView> {
  return request('/v1/publications/sync-center');
}

/**
 * Krok LIST1 — stav inzeratu zakazky a NABIDKA po rezervaci/prodeji. Nic se
 * neodesila bez `confirmCaseListingDeal` (zadani bod 3).
 */
export interface CaseListingDealPortal {
  readonly portal: string;
  readonly publicationId: string;
  readonly state: string | null;
  readonly live: boolean;
}

export interface CaseListingDeal {
  readonly caseId: string;
  readonly propertyId: string | null;
  readonly title: string | null;
  readonly status: ListingDealStatus;
  readonly dealState: 'RESERVED' | 'SOLD' | null;
  readonly soldKeepUntil: string | null;
  /** NAP2 A4: kdy planovac zaradil automaticke stazeni po uplynuti doby. */
  readonly soldKeepWithdrawnAt: string | null;
  readonly confirmedKind: 'RESERVED' | 'SOLD' | null;
  readonly confirmedAt: string | null;
  readonly portals: readonly CaseListingDealPortal[];
  readonly offer: {
    readonly kind: 'RESERVED' | 'SOLD';
    readonly defaultMode: 'WITHDRAW' | 'KEEP_SOLD' | null;
    readonly portals: readonly string[];
    readonly keepDefaultDays: number;
  } | null;
}

export function fetchCaseListingDeal(caseId: string): Promise<CaseListingDeal> {
  return request(`/v1/cases/${caseId}/listing-deal`);
}

export async function fetchCaseListingOffers(): Promise<readonly CaseListingDeal[]> {
  const data = await request<{ items: CaseListingDeal[] }>('/v1/listing-deals/offers');
  return data.items;
}

export function confirmCaseListingDeal(
  caseId: string,
  body: { kind: 'RESERVED' | 'SOLD'; mode?: 'WITHDRAW' | 'KEEP_SOLD'; keepDays?: number },
): Promise<{ readonly view: CaseListingDeal; readonly bulk: { readonly queued: number } }> {
  return request(`/v1/cases/${caseId}/listing-deal/confirm`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function createPublication(listingId: string, portal: string): Promise<PublicationDto> {
  return request('/v1/publications', { method: 'POST', body: JSON.stringify({ listingId, portal }) });
}

export function fetchPublication(id: string): Promise<PublicationDto> {
  return request(`/v1/publications/${id}`);
}

export function validatePublication(id: string): Promise<PublicationDto> {
  return request(`/v1/publications/${id}/validate`, { method: 'POST' });
}

export function fetchPreview(id: string): Promise<DryRunPreview> {
  return request(`/v1/publications/${id}/preview`);
}

export function queuePublication(id: string): Promise<PublicationDto> {
  return request(`/v1/publications/${id}/queue`, { method: 'POST' });
}

// ---- krok SR1: zivotni cyklus, hromadne akce, schvalovani ------------------

export type PortalOperation = 'UPDATE' | 'WITHDRAW' | 'RENEW' | 'TOP';

export function requestPortalOperation(
  id: string,
  body: { operation: PortalOperation; includePhotos?: boolean },
): Promise<PublicationDto> {
  return request(`/v1/publications/${id}/operations`, { method: 'POST', body: JSON.stringify(body) });
}

export function reopenPublication(id: string): Promise<PublicationDto> {
  return request(`/v1/publications/${id}/reopen`, { method: 'POST' });
}

export type BulkAction = 'EXPORT' | 'WITHDRAW' | 'DEAL_STATE' | 'ASSIGN_BROKER' | 'ARCHIVE';
export type BulkDealState = 'ACTIVE' | 'RESERVED' | 'SOLD';

export interface BulkActionBody {
  readonly action: BulkAction;
  readonly propertyIds: readonly string[];
  readonly portals?: readonly string[];
  readonly dealState?: BulkDealState;
  readonly brokerId?: string;
}

export interface BulkPlanItem {
  readonly propertyId: string;
  readonly caseId: string | null;
  readonly title: string | null;
  readonly portals: readonly string[];
  readonly skipped: readonly { readonly portal: string | null; readonly reason: string }[];
}

export interface BulkPlan {
  readonly action: BulkAction;
  readonly items: readonly BulkPlanItem[];
  readonly portalCalls: number;
  readonly affectedListings: number;
}

export interface BulkRunResult extends BulkPlan {
  readonly queued: number;
  readonly approvalRequests: readonly string[];
  readonly failures: readonly { readonly propertyId: string; readonly portal: string; readonly code: string }[];
}

export function previewBulkAction(body: BulkActionBody): Promise<BulkPlan> {
  return request('/v1/publications/bulk/preview', { method: 'POST', body: JSON.stringify(body) });
}

export function runBulkAction(body: BulkActionBody): Promise<BulkRunResult> {
  return request('/v1/publications/bulk', { method: 'POST', body: JSON.stringify(body) });
}

export async function fetchExportPolicy(): Promise<readonly string[]> {
  const data = await request<{ novices: string[] }>('/v1/connectors/export-policy');
  return data.novices;
}

export async function saveExportPolicy(
  userId: string,
  novice: boolean,
): Promise<readonly string[]> {
  const data = await request<{ novices: string[] }>('/v1/connectors/export-policy', {
    method: 'PUT',
    body: JSON.stringify({ userId, novice }),
  });
  return data.novices;
}

export interface ExportRequestDto {
  readonly id: string;
  readonly publicationId: string;
  readonly portal: string;
  readonly listingId: string;
  readonly operation: 'PUBLISH' | PortalOperation;
  readonly status: 'pending' | 'released' | 'rejected';
  readonly requestedBy: string;
  readonly requestedAt: string;
}

export async function fetchExportRequests(
  status: 'pending' | 'released' | 'rejected' = 'pending',
): Promise<readonly ExportRequestDto[]> {
  const data = await request<{ items: ExportRequestDto[] }>(
    `/v1/connectors/export-requests?status=${status}`,
  );
  return data.items;
}

export function decideExportRequest(
  id: string,
  decision: 'release' | 'reject',
): Promise<ExportRequestDto> {
  return request(`/v1/connectors/export-requests/${id}/decide`, {
    method: 'POST',
    body: JSON.stringify({ decision }),
  });
}

// ---- ceske popisky (UI vrstva; strojove kody drzi API) ----------------------

export const CELL_STATUS_LABELS: Readonly<Record<string, string>> = {
  DRAFT: 'Koncept',
  VALIDATED: 'Zvalidováno',
  QUEUED: 'Ve frontě',
  PUBLISHED: 'Publikováno',
  UPDATED: 'Aktualizováno',
  REMOVED: 'Staženo',
  ERROR: 'Chyba',
  NOT_CONNECTED: 'Nepřipojeno',
  NOT_PUBLISHED: 'Připraveno',
  PARTNER_REQUIRED: 'Vyžaduje partnera',
  PENDING_SPEC: 'Čeká na specifikaci',
};

export const PORTAL_STATUS_LABELS: Readonly<Record<string, string>> = {
  DOCS_VERIFIED: 'Dokumentace ověřena',
  PENDING_SPEC: 'Čeká na specifikaci',
  PARTNER_REQUIRED: 'Vyžaduje partnerskou cestu',
};

/** Technicky stav napojeni portalu (audit UXK4 bod 3 / nalez 26). Popisky pro
 * maklere nejsou tady: krok UXP7 mapuje tenhle stav na tri lidske vety
 * (`webAdmin.portaly.connectionStatus.*`) v `portaly.tsx` — `preview` je
 * z pohledu maklere taky „Nepripojeno". Technická připravenost
 * (`PORTAL_STATUS_LABELS`, verze dokumentace) je sekundární informace
 * v detailu, ne tenhle stav. */
export type PortalConnectionState = 'connected' | 'disconnected' | 'preview' | 'error';

export function connectionState(
  descriptor: PortalDescriptor,
  connection: ConnectionDto | undefined,
): PortalConnectionState {
  if (connection === undefined || !connection.enabled) return 'disconnected';
  if (connection.mode === 'DEMO') return 'preview';
  if (descriptor.auth.tenantCredentialFields.length > 0 && !connection.hasCredentials) return 'error';
  return 'connected';
}

/** Lidsky next step per canonical chyba (PORTAL-ERROR-TAXONOMY.md). */
export const ERROR_NEXT_STEPS: Readonly<Record<string, string>> = {
  AUTH: 'Zkontrolujte připojení portálu v Nastavení.',
  ACCOUNT_INACTIVE: 'Kontaktujte portál / obchod.',
  INSUFFICIENT_CREDIT: 'Dobijte kredit u portálu.',
  VALIDATION: 'Opravte inzerát podle detailu chyby.',
  BROKER_MISSING: 'Doplňte profil makléře a synchronizujte.',
  CATEGORY_INVALID: 'Upravte kategorii; ověřte číselník.',
  // Krok PORT6: Eurobydlení vyžaduje u každé fotky identifikátor (photo_rkid),
  // takže „opravte fotografie" musí pokrýt i chybějící údaj, ne jen formát.
  MEDIA_INVALID: 'Opravte fotografie (rozlišení, formát nebo chybějící údaj u fotky).',
  DUPLICATE: 'Spárujte inzerát s remote ID (reconciliation).',
  RATE_LIMIT: 'Automatická fronta - není třeba zasahovat.',
  TEMPORARY: 'Dočasná chyba portálu - automatický retry.',
  IMMUTABLE_FIELD: 'Hlavní položky už nelze měnit - založte nový inzerát, nebo ponechte.',
  NOT_FOUND: 'Inzerát na portálu neexistuje - proveďte reconciliation.',
  CONTRACT_REQUIRED: 'Je potřeba smlouva s portálem.',
  PARTNER_REQUIRED: 'Viz postup napojení portálu (onboarding).',
  UNKNOWN: 'Nenamapovaná chyba - eskalujte s uloženou odpovědí portálu.',
};

/**
 * Krok UXD1b — DALSI KROK PRO RADEK PUBLIKACE (ZBYVA z UXL1).
 *
 * Plochy radek Publikaci rika makleri nejen stav, ale i co s tim. Veta sama
 * zije v katalogu (`webAdmin.portaly.nextStep.<STAV>`), tady je jen
 * ROZHODNUTI, ktera veta ke kteremu radku patri — jedno misto pro celou
 * aplikaci. U chyby ma prednost `ERROR_NEXT_STEPS` (lidska veta per kanonicky
 * kod z PORTAL-ERROR-TAXONOMY.md), protoze „Dobijte kredit u portálu" je
 * konkretnejsi nez obecne „vyreste chybu".
 */
export function cellNextStep(
  cell: Pick<SyncCenterCell, 'status' | 'lastErrorCode'>,
  statusSentence: (status: string) => string,
): string {
  if (cell.status === 'ERROR' && cell.lastErrorCode !== null) {
    const byCode = ERROR_NEXT_STEPS[cell.lastErrorCode];
    if (byCode !== undefined) return byCode;
  }
  return statusSentence(cell.status);
}

